import React, { useEffect, useMemo, useState } from "react";
import { Box, Card, InputAdornment, TextField } from "@mui/material";
import Search from "@mui/icons-material/Search";
import StepControls from "../StepControls";
import mediaLibraryContent from "../mediaContent/mediaLibraryContent";
import "./index.scss";
import { TranslateProvider, useTranslation } from "../i18n";

const MediaLibrary = ({
  isShow = false,
  onClose,
  openPhotoViewer,
  openVideoViewer,
  openVRViewer,
  langCode = "en"
}) => {
  const {
    _t,
    language,
    setLanguage
  } = useTranslation();
  useEffect(() => {
    setLanguage(langCode);
  }, [langCode, setLanguage]);
  const mainCategoriesList = useMemo(() => mediaLibraryContent(language), [language]);
  const [selectedMainCategory, setSelectedMainCategory] = useState(null);
  const {
    subCategoryList,
    selectedCategoryTitle
  } = useMemo(() => {
    const searchedCategory = mediaLibraryContent(language).find(category => category.id === selectedMainCategory) || mediaLibraryContent(language)[0];

    if (searchedCategory?.sections) {
      return {
        subCategoryList: searchedCategory.sections,
        selectedCategoryTitle: searchedCategory.title
      };
    }

    return {
      subCategoryList: [],
      selectedCategoryTitle: ""
    };
  }, [language, selectedMainCategory]);
  const [selectedSubCategory, setSelectedSubCategory] = useState("");
  useEffect(() => {
    setSelectedSubCategory(subCategoryList[0].id);
  }, [subCategoryList]);
  const subCategoryContent = useMemo(() => {
    return subCategoryList.find(el => el.id === selectedSubCategory) || subCategoryList[0];
  }, [selectedSubCategory, subCategoryList]);
  const [searchContent, setSearchContent] = useState("");
  const searchContentAll = useMemo(() => {
    return subCategoryList.reduce((total, el) => {
      let middleValue = { ...total
      };

      if (el.video) {
        middleValue.video = [...middleValue.video, ...el.video];
      }

      if (el.photo) {
        middleValue.photo = [...middleValue.photo, ...el.photo];
      }

      if (el.vr) {
        middleValue.vr = [...middleValue.vr, ...el.vr];
      }

      return middleValue;
    }, {
      video: [],
      photo: [],
      vr: []
    });
  }, [subCategoryList]);
  const {
    video,
    photo,
    vr
  } = useMemo(() => {
    let mediaContent = {
      video: subCategoryContent.video || [],
      photo: subCategoryContent.photo || [],
      vr: subCategoryContent.vr || []
    };
    const searchedString = searchContent.toLowerCase();

    if (searchedString) {
      mediaContent.video = searchContentAll.video.filter(el => el.title.toLowerCase().match(searchedString));
      mediaContent.photo = searchContentAll.photo.filter(el => el.title.toLowerCase().match(searchedString));
      mediaContent.vr = searchContentAll.vr.filter(el => el.title.toLowerCase().match(searchedString));
    }

    return mediaContent;
  }, [searchContent, searchContentAll.photo, searchContentAll.video, searchContentAll.vr, subCategoryContent.photo, subCategoryContent.video, subCategoryContent.vr]);

  function goBack() {
    setSearchContent("");

    if (selectedMainCategory) {
      setSelectedMainCategory(null);
    } else {
      onClose(false);
    }
  }

  function previewVideo(el) {
    openVideoViewer(true, el.video, el.title);
  }

  function previewPhoto(index) {
    openPhotoViewer(true, photo, index);
  }

  function previewVR(el) {
    openVRViewer(true, el.folder, el.xCount, el.yCount);
  }

  if (!isShow) {
    return null;
  }

  return /*#__PURE__*/React.createElement("div", {
    className: "mediaLibrary"
  }, /*#__PURE__*/React.createElement(StepControls, {
    title: !selectedMainCategory ? _t("Выберите раздел стоматологии") : `${_t("Медиатека")} - ${selectedCategoryTitle}`,
    subTitle: !selectedMainCategory ? "<div class='controls-heading'>" + _t("Выберите из списка ниже") + "</div>" : null,
    goBack: goBack
  }), /*#__PURE__*/React.createElement("div", {
    className: "mediaLibrary-content"
  }, /*#__PURE__*/React.createElement("div", {
    className: "mediaLibrary-view"
  }, !selectedMainCategory ? /*#__PURE__*/React.createElement("div", {
    className: "mainCategoriesList"
  }, mainCategoriesList.map(category => /*#__PURE__*/React.createElement("button", {
    key: category.id,
    className: "mainCategoriesList-item",
    onClick: () => {
      setSelectedMainCategory(category.id);
    }
  }, /*#__PURE__*/React.createElement("img", {
    src: category.preview,
    className: "mainCategoriesList-item-preview",
    alt: ""
  }), /*#__PURE__*/React.createElement("div", {
    className: "mainCategoriesList-item-info"
  }, /*#__PURE__*/React.createElement("div", {
    className: "mainCategoriesList-item-info-title"
  }, category.title), /*#__PURE__*/React.createElement("div", {
    className: "mainCategoriesList-item-info-description"
  }, category.description))))) : /*#__PURE__*/React.createElement("div", {
    className: "subCategoryLayout"
  }, /*#__PURE__*/React.createElement("div", {
    className: "subCategoryListContainer"
  }, /*#__PURE__*/React.createElement("div", {
    className: "subCategoryList-title"
  }, _t("Разделы:")), /*#__PURE__*/React.createElement("div", {
    className: "subCategoryList"
  }, subCategoryList.map(subCategory => /*#__PURE__*/React.createElement("button", {
    key: subCategory.id,
    className: `subCategoryList-item ${!searchContent && subCategory.id === selectedSubCategory ? "active" : ""}`,
    onClick: () => {
      setSearchContent("");
      setSelectedSubCategory(subCategory.id);
    }
  }, /*#__PURE__*/React.createElement("img", {
    src: subCategory.preview,
    className: "subCategoryList-item-preview",
    alt: ""
  }), /*#__PURE__*/React.createElement("div", {
    className: "subCategoryList-item-title"
  }, subCategory.title))))), /*#__PURE__*/React.createElement(Box, {
    sx: {
      mx: 2
    },
    className: "subCategoryContent"
  }, /*#__PURE__*/React.createElement("div", {
    className: "subCategoryContent-search"
  }, /*#__PURE__*/React.createElement(TextField, {
    label: _t("Поиск по всем разделам"),
    sx: {
      mt: 1,
      mb: 2,
      bgcolor: "background.paper",
      width: "57ch",
      borderRadius: theme => theme.shape.borderRadius
    },
    type: "search",
    size: "small",
    onChange: e => setSearchContent(e.target.value),
    value: searchContent,
    InputProps: {
      startAdornment: /*#__PURE__*/React.createElement(InputAdornment, {
        position: "start"
      }, /*#__PURE__*/React.createElement(Search, null))
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: "subCategoryContent-parts"
  }, /*#__PURE__*/React.createElement("div", {
    className: "subCategoryContent-part"
  }, /*#__PURE__*/React.createElement(Card, {
    sx: {
      textAlign: "center",
      py: 1,
      mb: 2
    }
  }, _t("Видео")), /*#__PURE__*/React.createElement("div", {
    className: "subCategoryContent-part-list"
  }, video.map((el, index) => /*#__PURE__*/React.createElement("div", {
    key: index,
    className: "subCategoryContent-part-list-item",
    onClick: () => previewVideo(el)
  }, /*#__PURE__*/React.createElement("img", {
    src: el.videoPoster,
    className: "image",
    alt: ""
  }), /*#__PURE__*/React.createElement("div", {
    className: "title"
  }, el.title))))), /*#__PURE__*/React.createElement("div", {
    className: "subCategoryContent-part"
  }, /*#__PURE__*/React.createElement(Card, {
    sx: {
      textAlign: "center",
      py: 1,
      mb: 2
    }
  }, _t("Фото")), /*#__PURE__*/React.createElement("div", {
    className: "subCategoryContent-part-list"
  }, photo.map((el, index) => /*#__PURE__*/React.createElement("div", {
    key: index,
    className: "subCategoryContent-part-list-item",
    onClick: () => previewPhoto(index)
  }, /*#__PURE__*/React.createElement("img", {
    src: el.photo,
    className: "image",
    alt: ""
  }), /*#__PURE__*/React.createElement("div", {
    className: "title"
  }, el.title))))), /*#__PURE__*/React.createElement("div", {
    className: "subCategoryContent-part"
  }, /*#__PURE__*/React.createElement(Card, {
    sx: {
      textAlign: "center",
      py: 1,
      mb: 2
    }
  }, _t("3D")), /*#__PURE__*/React.createElement("div", {
    className: "subCategoryContent-part-list"
  }, vr.map((el, index) => /*#__PURE__*/React.createElement("div", {
    key: index,
    className: "subCategoryContent-part-list-item",
    onClick: () => previewVR(el)
  }, /*#__PURE__*/React.createElement("img", {
    src: el.preview,
    className: "image",
    alt: ""
  }), /*#__PURE__*/React.createElement("div", {
    className: "title"
  }, el.title)))))))))));
};

const MediaLibraryWrapper = props => {
  return /*#__PURE__*/React.createElement(TranslateProvider, null, /*#__PURE__*/React.createElement(MediaLibrary, props));
};

export default MediaLibraryWrapper;